'use client';

import { Checkbox, Divider, TextLabel, Typography } from '@musinsa/mds';
import { FormImageSelectField } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { forwardRef, useImperativeHandle } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { ClearableTextField } from '../../../common/components/ClearableTextField';
import { useSettlementForm } from '../../hooks/useSettlementForm';
import { AccountInfoSection } from './AccountInfoSection';
import { BusinessTypeField } from './BusinessTypeField';
import { IdNumberField } from './IdNumberField';
import { PrivacyPolicy } from './PrivacyPolicy';
import { SettlementFormProvider } from './SettlementFormContext';
import { SETTLEMENT_FORM_SCHEMA } from './settlement-form.schema';
import type { SettlementFormData } from './types';

interface SettlementFormProps {
  onSubmit: (data: SettlementFormData | Partial<SettlementFormData>) => void;
  defaultValues?: Partial<SettlementFormData>;
  isEditMode?: boolean;
  accountDisplayInfo?: {
    bankName?: string;
    accountNumber?: string;
    accountHolder?: string;
  };
  hasExistingAccountInfo?: boolean;
}

export interface SettlementFormRef {
  submit: () => void;
}

export const SettlementForm = forwardRef<SettlementFormRef, SettlementFormProps>(
  (
    { onSubmit, defaultValues, isEditMode = false, accountDisplayInfo, hasExistingAccountInfo },
    ref,
  ) => {
    const { methods, errors, accountValidation, handlers, config } = useSettlementForm({
      defaultValues,
      onSubmit,
      isEditMode,
      hasExistingAccountInfo,
    });

    // NOTE: 무신사 앱의 Android WebView에서는 accept 속성이 있으면 파일 선택기가
    // 제한적으로 동작하는 케이스가 보고되어, 해당 조건에서만 accept를 제거해
    // 파일시스템 전체에서 선택 가능하게 한다. 의도치 않은 파일 타입은 상위
    // useSettlementForm 핸들러에서 필터링한다.
    const { service, isWebview, isAndroid } = useDeviceInfo();
    const shouldBypassAccept = service === SERVICE.MUSINSA && isWebview && isAndroid;
    const imageAcceptAttr: string | null | undefined = shouldBypassAccept ? null : undefined;

    const { control, watch } = methods;
    const businessType = watch('businessType');

    useImperativeHandle(ref, () => ({
      submit: handlers.onSubmit,
    }));

    const contextValue = {
      accountValidation: {
        error: accountValidation.error,
        isValidating: accountValidation.isLoading,
        onVerify: handlers.onVerifyAccount,
      },
      handlers: {
        onAccountFieldChange: handlers.onAccountFieldChange,
        onAccountInputChange: handlers.onAccountInputChange,
      },
    };

    return (
      <FormProvider {...methods}>
        <SettlementFormProvider value={contextValue}>
          <form
            className="flex flex-col grow px-4 pb-[72px]!"
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <BusinessTypeField
              value={businessType}
              onChange={handlers.onBusinessTypeChange}
              error={!!errors.businessType}
              errorText={errors.businessType?.message}
            />

            {businessType === 'NATURAL_PERSON' && (
              <>
                {(!config.isEditMode || config.hasUnsavedTypeChange) && (
                  <Controller
                    name="idNumberFirst"
                    control={control}
                    render={({ field: firstField }) => (
                      <Controller
                        name="idNumberSecond"
                        control={control}
                        render={({ field: secondField }) => (
                          <IdNumberField
                            firstValue={firstField.value ?? ''}
                            secondValue={secondField.value ?? ''}
                            onFirstChange={handlers.onIdNumberChange(
                              'idNumberFirst',
                              firstField.onChange,
                            )}
                            onSecondChange={handlers.onIdNumberChange(
                              'idNumberSecond',
                              secondField.onChange,
                            )}
                            error={!!(errors.idNumberFirst || errors.idNumberSecond)}
                            errorText={
                              errors.idNumberFirst?.message || errors.idNumberSecond?.message
                            }
                          />
                        )}
                      />
                    )}
                  />
                )}

                <Controller
                  name="identityFile"
                  control={control}
                  render={({ field }) => (
                    <FormImageSelectField
                      titleContent="신분증"
                      id="identityFile"
                      value={
                        field.value && field.value instanceof File ? field.value.name : undefined
                      }
                      onChange={handlers.onIdentityFileChange}
                      error={!!errors.identityFile}
                      errorText={errors.identityFile?.message}
                      accept={imageAcceptAttr}
                      helpText={
                        !config.isEditMode || config.hasUnsavedTypeChange
                          ? '세금신고를 위한 필수 정보로 신분증 앞면 전체 정보 (증명사진 및 주민등록번호)가 보여질 수 있도록 업로드 해주세요.'
                          : undefined
                      }
                    />
                  )}
                />
              </>
            )}

            {businessType !== 'NATURAL_PERSON' && (
              <>
                <Controller
                  name="businessName"
                  control={control}
                  render={({ field }) => (
                    <ClearableTextField
                      {...field}
                      title="상호명"
                      placeholder="상호명을 입력해주세요"
                      error={!!errors.businessName}
                      errorText={errors.businessName?.message}
                    />
                  )}
                />

                <Controller
                  name="businessRegistrationNumber"
                  control={control}
                  render={({ field }) => (
                    <ClearableTextField
                      {...field}
                      title="사업자 등록 번호"
                      placeholder="000-00-00000"
                      inputMode="numeric"
                      maxLength={
                        SETTLEMENT_FORM_SCHEMA.validation.businessRegistrationNumber.maxLength
                      }
                      onChange={handlers.onBusinessRegistrationNumberChange(field.onChange)}
                      error={!!errors.businessRegistrationNumber}
                      errorText={errors.businessRegistrationNumber?.message}
                    />
                  )}
                />
                <Controller
                  name="businessLicenseFile"
                  control={control}
                  render={({ field }) => (
                    <FormImageSelectField
                      titleContent="사업자등록증"
                      id="businessLicenseFile"
                      value={
                        field.value && field.value instanceof File ? field.value.name : undefined
                      }
                      onChange={handlers.onBusinessLicenseFileChange}
                      error={!!errors.businessLicenseFile}
                      errorText={errors.businessLicenseFile?.message}
                      accept={imageAcceptAttr}
                    />
                  )}
                />
              </>
            )}

            <AccountInfoSection
              isViewMode={config.isAccountViewMode}
              accountDisplayInfo={accountDisplayInfo}
              onRequestAccountEdit={handlers.onRequestAccountEdit}
              onCancelAccountEdit={handlers.onCancelAccountEdit}
              canToggleAccountView={config.canToggleAccountView}
            />

            <Controller
              name="accountCopyFile"
              control={control}
              render={({ field }) => (
                <FormImageSelectField
                  key={businessType}
                  titleContent="계좌 사본"
                  id="accountCopyFile"
                  value={field.value && field.value instanceof File ? field.value.name : undefined}
                  onChange={handlers.onAccountCopyFileChange}
                  error={!!errors.accountCopyFile}
                  errorText={errors.accountCopyFile?.message}
                  accept={imageAcceptAttr}
                />
              )}
            />

            <div className="w-full pt-4 pb-3">
              <Divider variant="line" color="gray-500" bgOpacity="10" />
            </div>

            <div className="flex items-center justify-between py-3">
              <div className="flex items-center space-x-1">
                <Controller
                  name="termsAgreed"
                  control={control}
                  render={({ field }) => (
                    <Checkbox checked={field.value || false} onCheckedChange={field.onChange} />
                  )}
                />
                <TextLabel>
                  <Typography variant="body13pxReg" colorScheme="gray-600">
                    개인정보 수집 및 이용 동의 (필수)
                  </Typography>
                </TextLabel>
              </div>

              <PrivacyPolicy />
            </div>

            {errors.termsAgreed && (
              <Typography variant="textEtc11pxReg" colorScheme="red" className="pb-3">
                {errors.termsAgreed.message || '정산 정보 수집 이용 동의를 해주세요.'}
              </Typography>
            )}
          </form>
        </SettlementFormProvider>
      </FormProvider>
    );
  },
);

SettlementForm.displayName = 'SettlementForm';
